<template>
  <div class="p-4 fade-in max-w-screen-xl w-full mx-auto">
    <!-- Breadcrumbs -->
    <Breadcrumbs :items="breadcrumbItems" />

    <!-- Top boxes -->
    <div class="flex flex-wrap gap-4 justify-between my-2">
      <!-- Left Side: User Info and Progress Stats -->
      <div class="flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 fadeIn h-fit">
        <ExpeditionUserCard :expedition-id="expeditionId" />
        <hr class="border-gray-200" />
        <ProgressStats :user-data="expeditionsStore.userData" class="min-w-[28rem]" />
      </div>

      <!-- Right Side: Week Navigator and Notes -->
      <div
        class="flex flex-col flex-1 max-w-3xl bg-white rounded-xl shadow-sm border border-gray-200"
      >
        <ManageWeekNavigator
          :current-week="expeditionsStore.currentWeek"
          :selected-week="viewedWeek"
          :expedition-id="expeditionId"
          :user-id="userId"
          @week-selected="handleWeekSelected"
          class="ml-auto"
        />

        <hr class="border-gray-200" />

        <!-- Notes Section -->
        <UserNoteCard
          :notes="userNotes"
          :week="viewedWeek"
          :is-loading="isNotesLoading"
          @save-note="handleSaveNote"
        />
      </div>
    </div>

    <ManageCurriculumTable
      :items="stableItems"
      :is-loading="isLoading"
      :is-notes-loading="false"
      :is-updating="false"
      :personal-notes="{}"
    />
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { useExpeditionsStore } from '@/stores/expeditions'
import { watch, watchEffect, computed, ref } from 'vue'
import ExpeditionUserCard from '@/components/manage/ExpeditionUserCard.vue'
import ManageWeekNavigator from '@/components/manage/ManageWeekNavigator.vue'
import ManageCurriculumTable from '@/components/manage/ManageCurriculumTable.vue'
import ProgressStats from '@/components/dashboard/ProgressStats.vue'
import UserNoteCard from '@/components/manage/UserNoteCard.vue'
import Breadcrumbs from '@/components/ui/Breadcrumbs.vue'
import { notesService } from '@/services/notes'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const route = useRoute()
const router = useRouter()
const expeditionId = computed(() => route.params.id as string)
const userId = computed(() => route.params.userId as string)

const expeditionsStore = useExpeditionsStore()
const { isBlurred } = usePrivacyMode()

// State for the week being viewed
const viewedWeek = ref<number | null>(null)

// Ref to hold the items currently displayed in the table
const stableItems = ref<any[]>([]) // Initialize as empty array

// Notes state
const userNotes = ref<any[]>([])
const isNotesLoading = ref(false)

// Directly compute loading state from store
const isLoading = computed(() => expeditionsStore.isLoading ?? false)

// Breadcrumb items
const breadcrumbItems = computed(() => [
  {
    label: 'Expedition Progress',
    to: `/manage/${expeditionId.value}`
  },
  {
    label: `${expeditionsStore.userData?.first_name || ''} ${expeditionsStore.userData?.last_name || ''}`.trim() || 'User',
    to: `/manage/${expeditionId.value}/${userId.value}`,
    blur: isBlurred.value
  }
])

watch(expeditionId, async () => {
  await expeditionsStore.fetchCurrentWeek(expeditionId.value)

  const weekFromQuery = route.query.week
  if (weekFromQuery && typeof weekFromQuery === 'string' && /^[1-9]\d*$/.test(weekFromQuery)) {
    viewedWeek.value = parseInt(weekFromQuery)
  } else {
    viewedWeek.value = expeditionsStore.currentWeek
  }
}, { immediate: true })

// Watch for changes in route query
watch(
  () => route.query.week,
  (newWeek) => {
    if (newWeek && typeof newWeek === 'string' && /^[1-9]\d*$/.test(newWeek)) {
      viewedWeek.value = parseInt(newWeek)
    }
  }
)

// Fetch progress when viewedWeek changes
watchEffect(() => {
  const weekToFetch = viewedWeek.value
  if (weekToFetch !== null && weekToFetch !== undefined) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    expeditionsStore.fetchAdventurerProgressByWeek(expeditionId.value, userId.value, weekToFetch.toString())
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadNotes(weekToFetch)
  }
})

// Load notes for the current user and week
const loadNotes = async (week: number) => {
  if (!week) return

  isNotesLoading.value = true
  try {
    const data = await notesService.getNotesByExpeditionAndWeek(expeditionId.value, week.toString())

    if (data && Array.isArray(data)) {
      // Filter notes for the current user
      userNotes.value = data.filter((note: any) => note.adventurer === parseInt(userId.value))
    } else {
      userNotes.value = []
    }
  } catch (error) {
    console.error('Error loading notes:', error)
    userNotes.value = []
  } finally {
    isNotesLoading.value = false
  }
}

// Handle note saving
const handleSaveNote = async (note: string) => {
  try {
    const existingNotes = userNotes.value || []
    const noteText = (note || '').trim()

    if (!noteText) {
      if (existingNotes.length > 0) {
        for (const noteToDelete of existingNotes) {
          await notesService.deleteNote(noteToDelete.id)
        }
      }
    } else {
      if (existingNotes.length > 0) {
        await notesService.updateNote(existingNotes[0].id, { note: noteText })
        for (let i = 1; i < existingNotes.length; i++) {
          await notesService.deleteNote(existingNotes[i].id)
        }
      } else {
        await notesService.createNote({
          adventurer: parseInt(userId.value),
          expedition: expeditionId.value,
          week: viewedWeek.value!,
          note: noteText
        })
      }
    }
    if (viewedWeek.value) {
      await loadNotes(viewedWeek.value)
    }
  } catch (error) {
    console.error('Error saving note:', error)
  }
}

// Watch for changes in the store's progress data
watch(
  () => expeditionsStore.adventurerWeeklyProgress,
  (newProgressData) => {
    // Only update the displayed items when loading is finished
    if (!isLoading.value) {
      stableItems.value = newProgressData?.progress ?? []
    }
    // While loading, stableItems retains its previous value
  },
  { deep: true, immediate: true }
)

// Update document title when user data is available
watchEffect(() => {
  if (expeditionsStore.userData) {
    const userFullName =
      `${expeditionsStore.userData.first_name} ${expeditionsStore.userData.last_name}`.trim()
    document.title = `${userFullName} Progress | Outtalent Campus`
  }
})

// Handler for the event from WeekManageNavigator
const handleWeekSelected = (selectedWeekString: string | null) => {
  if (selectedWeekString === null) {
    return
  }
  const selectedWeekNumber = parseInt(selectedWeekString)
  if (!isNaN(selectedWeekNumber)) {
    viewedWeek.value = selectedWeekNumber
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({ query: { ...route.query, week: selectedWeekNumber.toString() } })
  }
}
</script>
